import React, { useEffect, useMemo, useState } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { toast } from "react-toastify";

import { formatCurrency } from "../../../core/currency.core";
import { useTranslation } from "../../../core/translation.core";
import useCart from "../../../hooks/services/useCart.hook";
import posIcons from "../../../icons/pos.icons";
import { CartItem, CartItemTopUpSuggestion } from "../../../types/cart";
import { WarehouseInventory } from "../../../types/inventory";
import { Product } from "../../../types/product";
import Numpad from "../../common/numpad.comp";
import { FancyTextInput } from "../../reuseables/form.comp";
import PlusIcon from "../../../icons/posicons/plus.icon";
import MinusIcon from "../../../icons/posicons/minus.icon";
import { CustomSuggestionOption } from "../../../types/posProduct";
import localeCore from "../../../core/locale.core";
import useAdminShop from "../../../hooks/useAdminShop";

interface CartProductProps {
  item: CartItem;
  removeItem: ({ productId, cartItemId }: { productId: string; cartItemId: string }) => void;
  updateItem: ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => void;
}

export default function CartProduct({ item, removeItem }: CartProductProps) {
  const [showEdit, setShowEdit] = useState(false);

  const itemPrice = useMemo(() => {
    const cartPrice = item.itemPrice;
    return item.quantity * cartPrice;
  }, [item]);

  const overidePrice = useMemo(() => {
    const cartPrice = item.discountPrice ? item.discountPrice : item.overridePrice != null ? item.overridePrice : item.itemPrice;
    return item.quantity * cartPrice;
  }, [item]);

  const recycleDeposit = useMemo(() => {
    return item.quantity * (item.product.recycleDeposit || 0);
  }, [item]);

  const handleToggleEdit = () => {
    setShowEdit(!showEdit);
  };

  if (!item) return null;

  return (
    <div id="cart-product" className="dark:bg-her-backblue bg-her-lightgray">
      <div className="dark:bg-her-backblue flex jusitfy-between items-center cursor-pointer bg-her-lightgray px-3 h-full lg:min-h-[56px] rounded-lg space-y-2 lg:space-y-0 py-2 lg:py-0">
        <div onClick={handleToggleEdit} className="w-[85%]">
          <div className="flex justify-between items-center">
            <button id="show-edit">
              {!showEdit ? <span>{posIcons.arrowRightIcon}</span> : <span>{posIcons.arrowDownIcon}</span>}
            </button>
            <div className="flex items-center w-[75%]">
              <div className="font-bold text-sm flex items-center gap-2 space-y-0 w-full">
                <span className="text-base">
                  {item.quantity}
                  <span>x</span>
                </span>

                <h1 className="text-ellipsis overflow-hidden truncate max-w-full">{item?.product?.name}</h1>
              </div>
            </div>

            <div className="flex justify-between gap-2 text-sm grow-1">
              <div data-price={itemPrice} className="flex flex-col items-center font-bold">
                <h4
                  className={`
                  ${item.overridePrice != null && itemPrice != overidePrice && "line-through text-[10px]"}
                  ${getPriceTextSize(itemPrice.toString())}
                `}
                >
                  {formatCurrency(itemPrice, item.product.currency)}
                </h4>

                {item.overridePrice != null && itemPrice != overidePrice && (
                  <h4 data-override-price={overidePrice} className={`${getPriceTextSize(overidePrice.toString())}`}>
                    {formatCurrency(overidePrice, item.product.currency)}
                  </h4>
                )}
              </div>
            </div>
          </div>
          <div className="w-full">
            {item?.customization ? <ProductCustomization customSuggestionOptions={item?.customization?.options} /> : null}
            {item.topUps?.length > 0 ? <ProductTopUps topUps={item.topUps} /> : null}
          </div>
        </div>

        <button
          type="button"
          id="delete-btn"
          onClick={() => removeItem({ cartItemId: item.id, productId: item.product.id })}
          className="justify-self-end pl-3 pr-1 sm:pl-6 sm:pr-3"
        >
          {posIcons.productCancelIcon}
        </button>
      </div>

      {showEdit && <EditProduct product={item.product} item={item} recycleDeposit={recycleDeposit} />}
    </div>
  );
}

const ProductCustomization = ({ customSuggestionOptions }: { customSuggestionOptions: CustomSuggestionOption[] }) => {
  const { t, msg } = useTranslation();

  return (
    <div className="flex text-sm text-her-blue">
      {t(msg.product.customization)}: {customSuggestionOptions.map((c) => c.value).join(" | ")}
    </div>
  );
};

const ProductTopUps = ({ topUps }: { topUps: CartItemTopUpSuggestion[] }) => {
  const { t, msg } = useTranslation();

  return (
    <div className="text-sm text-her-blue">
      {t(msg.product.topUps)}: {topUps.map((t) => localeCore.getLocaleValueDefault(t.name)).join(" | ")}
    </div>
  );
};

function getPriceTextSize(price: string) {
  if (price.length > 7) return "text-xs";
  return "text-sm";
}

interface EditProductProps {
  item: CartItem;
  product: Product;
  recycleDeposit: number;
}

interface FormType {
  note: string | null;
  quantity: number;
  warehouseId: string;
  discountPrice: string | null;
  discountedPrice: string | null;
  discountPercentage: string | null;
}

function EditProduct({ product, item, recycleDeposit }: EditProductProps) {
  const { updateItem, overrideProductPriceMutation, addNote, removePriceOverrideMutation } = useCart();
  const { msg, t } = useTranslation();
  const shop = useAdminShop();
  const [isOveride, setIsOveride] = useState<boolean>();
  const useDiscountedPriceField = shop?.discountField === "discountedPrice";

  const methods = useForm<FormType>({
    defaultValues: {
      note: item.productNote || null,
      quantity: item.quantity,
      warehouseId: item.warehouseId,
    },
  });

  const [quantity, warehouseId] = useWatch({ control: methods.control, name: ["quantity", "warehouseId", "discountPrice"] });

  useEffect(() => {
    if (quantity !== undefined && quantity >= 0) {
      updateItem({ cartItemId: item.id, product, quantity, warehouseId });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, updateItem, product]);

  const overridePrice = async () => {
    setIsOveride(false);
    await removePriceOverrideMutation.mutate({ productId: product.id });

    try {
      methods.setValue("discountPercentage", null);
      methods.setValue("discountedPrice", null);
    } catch (error) {
      console.log(error);
    } finally {
      const applyingDiscount = methods.getValues("discountPrice");
      if (applyingDiscount !== null && applyingDiscount !== "" && Number(applyingDiscount) >= 0) {
        overrideProductPriceMutation.mutate({ applyingDiscount: Number(applyingDiscount), productId: product.id });
      }
      setIsOveride(true);
    }
  };

  const overrideDiscountedPrice = async () => {
    setIsOveride(false);
    await removePriceOverrideMutation.mutate({ productId: product.id });

    try {
      methods.setValue("discountPercentage", null);
      methods.setValue("discountPrice", null);
    } catch (error) {
      console.log(error);
    } finally {
      const discountedPrice = methods.getValues("discountedPrice");
      if (discountedPrice !== null && discountedPrice !== "" && Number(discountedPrice) >= 0) {
        overrideProductPriceMutation.mutate({ discountedPrice: Number(discountedPrice), productId: product.id });
      }
      setIsOveride(true);
    }
  };

  async function overridePriceDiscount() {
    setIsOveride(false);

    try {
      methods.setValue("discountPrice", null);
      methods.setValue("discountedPrice", null);
    } catch (error) {
      console.log(error);
    } finally {
      // first round to 2 decimal places then ceil it.
      // weird case: 1080 * 0.7 = 324.00000000000006.  Ceil -> 325!!
      // don't use Ceil now.  If item price is 71.15.  Ceil will make it to 72, and then mulitple quantity. the total will be off.
      const discountedPrice = Number((item.itemPrice * (1 - Number(discount!) / 100)).toFixed(2));

      const discountPercentage = Math.round(Number(discount!));
      methods.setValue("discountPercentage", String(discountPercentage));

      // Update the override price with the discounted price
      await overrideProductPriceMutation.mutate({ price: discountedPrice, productId: product.id });
    }
  }

  const resetPrice = async () => {
    methods.setValue("discountPercentage", null);
    methods.setValue("discountPrice", null);
    methods.setValue("discountedPrice", null);

    await removePriceOverrideMutation.mutate({ productId: product.id });
  };

  const discount = useWatch({ control: methods.control, name: "discountPercentage" });

  const addNoteToProduct = async (note: string) => {
    await addNote.mutate({ note, productId: product.id });
  };

  const incrementQuantity = () => {
    const newQuantity = quantity + 1;
    methods.setValue("quantity", newQuantity);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      // Prevents quantity from going below 1
      const newQuantity = quantity - 1;
      methods.setValue("quantity", newQuantity);
    } else {
      toast.error("Quantity cannot be less than 1");
    }
  };

  const btnCss = "w-10 h-10 py-2 flex justify-center items-center bg-[#E3E3E3] hover:bg-her-blue hover:text-white";

  return (
    <div className="pt-3 pb-6 px-3 space-y-6">
      {recycleDeposit > 0 ? (
        <div>
          <div className="text-sm">
            {t(msg.cart.recycleDeposit)}: {formatCurrency(recycleDeposit, item.product.currency)}
          </div>
        </div>
      ) : null}
      <section>
        <div className="grid grid-cols-3 gap-4 pb-2 w-full">
          <div className="space-y-1">
            <label className="text-sm dark:text-white">{t(msg.formLabels.quantity)}</label>

            <div className="text-black font-bold w-full rounded-md flex items-center">
              <button onClick={decrementQuantity} className={`btn-minus ${btnCss} rounded-l-md`}>
                <MinusIcon className="w-5 h-5" />
              </button>
              <span className="bg-white basis-1/2 text-center h-10 py-2 text-lg">{quantity}</span>
              <button onClick={incrementQuantity} className={`btn-plus ${btnCss} rounded-r-md`}>
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>

            {item.overridePrice != null || discount ? (
              <button onClick={() => resetPrice()} className="text-xs mb-4 mt-2 bg-red-700 px-3 py-1 rounded-sm shadow-md">
                {t(msg.cart.removeDiscount)}
              </button>
            ) : null}
          </div>

          <Numpad
            onClick={() => overridePriceDiscount()}
            methods={methods}
            maxLength={2}
            name="discountPercentage"
            label={t(msg.formLabels.discount)}
            placeholder={t(msg.formLabels.enterDiscount)}
            className={inputClass}
          />

          {useDiscountedPriceField ? (
            <Numpad
              methods={methods}
              name="discountedPrice"
              onClick={() => overrideDiscountedPrice()}
              maxLength={4}
              label={t(msg.formLabels.discountedPrice)}
              placeholder={t(msg.formLabels.enterDiscountedPrice)}
              className={inputClass}
            />
          ) : (
            <Numpad
              methods={methods}
              name="discountPrice"
              onClick={() => overridePrice()}
              maxLength={4}
              label={t(msg.formLabels.discountPrice)}
              placeholder={t(msg.formLabels.enterDiscountPrice)}
              className={inputClass}
            />
          )}
        </div>

        <div>
          <FancyTextInput
            register={() =>
              methods.register("note", {
                onChange: (e) => addNoteToProduct(e.target.value),
              })
            }
            placeholder={t(msg.formLabels.Typetoaddnotes)}
          />
        </div>
      </section>

      <WarehouseSelector retailInventories={item.retailInventories} />
    </div>
  );
}

// TODO: Activate selector when we implement multiple warehouses per store
const WarehouseSelector = ({ retailInventories }: { retailInventories: WarehouseInventory[] }) => {
  if (retailInventories?.length <= 1) {
    return null;
  }

  return (
    <label className="text-gray-700 space-y-2">
      <span className="block dark:text-white">Warehouses:</span>

      <select className="p-2 rounded-md" placeholder="Select Warehouse">
        {retailInventories?.map((i) => (
          <option key={i.warehouseId} value={i.warehouseId}>
            {i.warehouseName}: {i.count}
          </option>
        ))}
      </select>
    </label>
  );
};

const inputClass = "h-11 border rounded-md outline-none w-full my-3 px-2 mb-1 dark:text-gray-500 placeholder:text-xs  ";
